import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Pill, Plus, Upload, Trash2, X } from "lucide-react";

const TYPES = ["Tablet", "Capsule", "Syrup", "Injection", "Drops", "Cream"];

export default function VetMedicines() {
  const [meds, setMeds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "Tablet", strength: "", default_dose: "", notes: "" });
  const [csvText, setCsvText] = useState("");

  const load = () => axios.get(`${API}/partner/vet/medicines`).then(r => setMeds(r.data.medicines));
  useEffect(() => { load(); }, []);

  const addOne = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/partner/vet/medicines`, form);
    setForm({ name: "", type: "Tablet", strength: "", default_dose: "", notes: "" });
    setOpen(false);
    load();
  };

  const del = async (id) => {
    if (!confirm("Remove this medicine?")) return;
    await axios.delete(`${API}/partner/vet/medicines/${id}`);
    load();
  };

  const parseCsv = (text) => {
    // Headers expected: name,type,strength,default_dose,notes
    const lines = text.trim().split(/\r?\n/);
    const header = lines.shift().split(",").map(s => s.trim().toLowerCase());
    return lines.filter(Boolean).map(line => {
      const cols = line.split(",").map(s => s.trim());
      const obj = {};
      header.forEach((h, i) => { obj[h] = cols[i] || ""; });
      return {
        name: obj.name || "",
        type: obj.type || "Tablet",
        strength: obj.strength || null,
        default_dose: obj.default_dose || null,
        notes: obj.notes || null,
      };
    }).filter(m => m.name);
  };

  const uploadCsv = async (e) => {
    e.preventDefault();
    const parsed = parseCsv(csvText);
    if (parsed.length === 0) return alert("No valid rows found.");
    await axios.post(`${API}/partner/vet/medicines/bulk`, parsed);
    setCsvText("");
    load();
  };

  const sampleCsv = "name,type,strength,default_dose,notes\nAmoxicillin,Tablet,250mg,1 tab BID for 5 days,With food\nMetronidazole,Tablet,200mg,1 tab BID for 5 days,\nPraziquantel,Tablet,50mg,1 tab × 1 dose,Deworming";

  return (
    <div className="space-y-6" data-testid="vet-medicines-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Medicine catalogue</h1>
          <p className="text-ink-soft text-sm">Build your prescribing list — manually or via CSV.</p>
        </div>
        <button data-testid="add-med-button" onClick={() => setOpen(true)} className="bg-brand text-white rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover">
          <Plus className="w-4 h-4"/> Add medicine
        </button>
      </div>

      <details className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="csv-upload">
        <summary className="font-heading text-lg font-bold cursor-pointer flex items-center gap-2">
          <Upload className="w-4 h-4"/> Bulk import via CSV
        </summary>
        <form onSubmit={uploadCsv} className="mt-3">
          <p className="text-xs text-ink-soft mb-2">Headers: <code>name,type,strength,default_dose,notes</code></p>
          <textarea
            data-testid="csv-textarea"
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
            rows={6}
            placeholder={sampleCsv}
            className="w-full bg-surface-muted border border-line rounded-2xl px-3 py-2 font-mono text-xs outline-none focus:border-brand"
          />
          <div className="mt-2 flex gap-2 flex-wrap">
            <button data-testid="csv-import-button" disabled={!csvText.trim()} className="bg-ink text-white rounded-full px-4 py-2 text-sm font-bold disabled:opacity-50">Import</button>
            <button type="button" onClick={() => setCsvText(sampleCsv)} className="text-sm text-brand font-bold">Load sample</button>
          </div>
        </form>
      </details>

      <div className="bg-surface border border-line rounded-3xl shadow-soft overflow-hidden">
        {meds.length === 0 ? (
          <div className="p-8 text-center text-ink-soft text-sm">
            <Pill className="w-10 h-10 mx-auto mb-2 text-brand"/>
            No medicines yet. Add your first one to start prescribing.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-left">
              <tr>
                <th className="px-4 py-3 font-bold">Name</th>
                <th className="px-4 py-3 font-bold">Type</th>
                <th className="px-4 py-3 font-bold">Strength</th>
                <th className="px-4 py-3 font-bold">Default dose</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {meds.map(m => (
                <tr key={m.medicine_id} className="border-t border-line" data-testid={`med-${m.medicine_id}`}>
                  <td className="px-4 py-3 font-bold">{m.name}</td>
                  <td className="px-4 py-3 text-ink-soft">{m.type}</td>
                  <td className="px-4 py-3 text-ink-soft">{m.strength || "—"}</td>
                  <td className="px-4 py-3 text-ink-soft">{m.default_dose || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(m.medicine_id)} className="p-1.5 rounded-full hover:bg-danger/10 text-danger"><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={addOne} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="add-med-form">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl font-extrabold">Add medicine</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
            </div>
            <div className="space-y-3">
              <F label="Name"><input data-testid="med-name" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="li" placeholder="Amoxicillin"/></F>
              <F label="Type">
                <select data-testid="med-type" value={form.type} onChange={e=>setForm({...form, type:e.target.value})} className="li">{TYPES.map(t => <option key={t}>{t}</option>)}</select>
              </F>
              <F label="Strength"><input data-testid="med-strength" value={form.strength} onChange={e=>setForm({...form, strength:e.target.value})} className="li" placeholder="250mg"/></F>
              <F label="Default dose"><input data-testid="med-dose" value={form.default_dose} onChange={e=>setForm({...form, default_dose:e.target.value})} className="li" placeholder="1 tab BID × 5 days"/></F>
              <F label="Notes"><input data-testid="med-notes" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} className="li" placeholder="With food"/></F>
            </div>
            <button data-testid="med-save" className="mt-5 w-full bg-brand text-white rounded-full py-3 font-bold hover:bg-brand-hover">Save medicine</button>
            <style>{`.li{width:100%;border:1px solid #EAE0D5;border-radius:1.5rem;padding:0.6rem 1rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.li:focus{border-color:#FF6B35;}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
